// DonutChart — part-to-whole for a handful of categories. Categorical
// palette by slot (identity), a 2px surface gap between segments so
// neighbors read distinct, a center total, and a legend with values
// (identity never color-alone). Keep to ≤6 slices; fold the rest into
// "Other".
//
//   <DonutChart
//     segments={[{ label: 'Active', value: 42 }, { label: 'Pending', value: 12 }, …]}
//     centerLabel="Placements"
//     ariaLabel="Placements by status"
//   />

import { seriesColor, compact } from './chartUtils';

export interface DonutSegment { label: string; value: number; color?: string }

export interface DonutChartProps {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  /** Override the big center number (defaults to the sum). */
  centerValue?: string;
  ariaLabel: string;
  className?: string;
}

function arc(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const p0 = { x: cx + r * Math.cos(a0), y: cy + r * Math.sin(a0) };
  const p1 = { x: cx + r * Math.cos(a1), y: cy + r * Math.sin(a1) };
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A${r} ${r} 0 ${large} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
}

export default function DonutChart({
  segments,
  size = 168,
  thickness = 22,
  centerLabel,
  centerValue,
  ariaLabel,
  className,
}: DonutChartProps) {
  const total = segments.reduce((s, d) => s + Math.max(0, d.value), 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - thickness) / 2;
  const gap = total > 0 ? 0.03 : 0; // radians of surface gap between segments

  let angle = -Math.PI / 2; // start at 12 o'clock
  const arcs = segments.map((seg, i) => {
    const frac = total > 0 ? Math.max(0, seg.value) / total : 0;
    const a0 = angle + (frac > 0 ? gap / 2 : 0);
    const a1 = angle + frac * Math.PI * 2 - (frac > 0 ? gap / 2 : 0);
    angle += frac * Math.PI * 2;
    return { d: a1 > a0 ? arc(cx, cy, r, a0, a1) : '', color: seg.color ?? seriesColor(i) };
  });

  return (
    <div className={['ui-donut', className].filter(Boolean).join(' ')}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={ariaLabel} style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--chart-track)" strokeWidth={thickness} />
        {arcs.map((a, i) => a.d && (
          <path key={i} d={a.d} fill="none" stroke={a.color} strokeWidth={thickness} strokeLinecap="butt" />
        ))}
        <text x={cx} y={cy - 2} textAnchor="middle" className="ui-donut__center-value ui-chart-value">
          {centerValue ?? compact(total)}
        </text>
        {centerLabel && (
          <text x={cx} y={cy + 15} textAnchor="middle" className="ui-donut__center-label">
            {centerLabel}
          </text>
        )}
      </svg>
      <div className="ui-chart-legend" style={{ flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {segments.map((seg, i) => (
          <span key={i} className="ui-legend-item" style={{ justifyContent: 'space-between', minWidth: 150 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              <span className="ui-legend-swatch" style={{ background: seg.color ?? seriesColor(i) }} />
              {seg.label}
            </span>
            <span className="ui-chart-value" style={{ color: 'var(--text-strong)', fontWeight: 600 }}>
              {compact(seg.value)}
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>
                {total > 0 ? `${Math.round((seg.value / total) * 100)}%` : '—'}
              </span>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
